import { styled } from "goober";
import type { ReactNode } from "react";
import type { DisplayableSpace } from "../../hooks/useSpaceDisplayName";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { Icon, type IconName } from "../Icon";
import { PressButton } from "../PressButton";
import { SpaceBadge } from "../SpaceBadge";
import { Text } from "../Text";
import { monitoringInvestigationHover } from "./AskMonitoringChat";
import { MONITORING_PANEL_PADDING } from "./monitoringPanelLayout";

const cardBase = `
  ${monitoringInvestigationHover}
  position: relative;
  overflow: hidden;
  width: 100%;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: ${MONITORING_PANEL_PADDING}px;
  border-radius: 12px;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
`;

const interactiveCardStyle = `
  cursor: pointer;

  &:hover {
    background:
      linear-gradient(var(--alpha4), var(--alpha4)), var(--surface-secondary);
  }
  &:hover [data-deck="back"] {
    transform: translate(-4px, -3px);
  }
  &:hover [data-deck="front"] {
    transform: translate(4px, -8px);
  }
`;

const Card = styled(PressButton)`
  ${cardBase}
  ${interactiveCardStyle}

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

const CompositeCard = styled("div")`
  ${cardBase}
  ${interactiveCardStyle}
`;

const CardAction = styled(PressButton)`
  position: absolute;
  inset: 0;
  z-index: 2;
  width: 100%;
  height: 100%;

  &&&:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: -2px;
  }
`;

const StaticCard = styled("div")`
  ${cardBase}
`;

const CardHeader = styled("div")`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

// Stacked layout: title with the accessory directly beneath it, no big total.
const StackedContent = styled("div")`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  width: 100%;
`;

const CardTotalRow = styled("div")`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const TitleRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const InteractiveTitleAccessory = styled("span")`
  position: relative;
  z-index: 3;
  display: inline-flex;
`;

export function SummaryCard({
  title,
  value,
  valueLabel,
  onOpen,
  accessory,
  titleAccessory,
  interactiveTitleAccessory,
  cornerAction,
  stacked,
}: {
  title: string;
  value?: ReactNode;
  valueLabel?: ReactNode;
  onOpen?: () => void;
  accessory?: ReactNode;
  titleAccessory?: ReactNode;
  interactiveTitleAccessory?: boolean;
  cornerAction?: ReactNode;
  stacked?: boolean;
}) {
  const renderedTitleAccessory = interactiveTitleAccessory ? (
    <InteractiveTitleAccessory>{titleAccessory}</InteractiveTitleAccessory>
  ) : (
    titleAccessory
  );
  const content = stacked ? (
    <StackedContent>
      <TitleRow>
        <Text variant="text-small-md">{title}</Text>
        {renderedTitleAccessory}
      </TitleRow>
      {accessory}
    </StackedContent>
  ) : (
    <>
      <CardHeader>
        <TitleRow>
          <Text variant="text-small-md">{title}</Text>
          {renderedTitleAccessory}
        </TitleRow>
        <CardTotalRow>
          <Text variant="display-md">{value}</Text>
          <Text variant="text-regular" color="var(--text-secondary)">
            {valueLabel}
          </Text>
        </CardTotalRow>
      </CardHeader>
      {accessory ??
        (onOpen && (
          <Icon name="chevron-right" size={16} color="var(--text-secondary)" />
        ))}
    </>
  );
  if (onOpen && (interactiveTitleAccessory || cornerAction)) {
    return (
      <CompositeCard data-no-pill>
        <CardAction data-no-pill onPress={onOpen} aria-label={`Open ${title}`}>
          <span aria-hidden />
        </CardAction>
        {content}
        {cornerAction}
      </CompositeCard>
    );
  }
  return onOpen ? (
    <Card data-no-pill onPress={onOpen}>
      {content}
    </Card>
  ) : (
    <StaticCard data-no-pill>
      {content}
      {cornerAction}
    </StaticCard>
  );
}

export const DETAIL_MODAL_STYLE = {
  width: 680,
  height: 760,
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
} as const;

export const ModalHeader = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
`;

const ModalHeaderLeft = styled("div")`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ModalHeaderRight = styled("div")`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

// A drill-in modal's title: an icon badge for the section, the section name,
// and an optional space badge for context when the modal is scoped to a space.
export function ModalTitle({
  icon,
  title,
  space,
}: {
  icon: IconName;
  title: string;
  space?: DisplayableSpace;
}) {
  return (
    <ModalHeaderLeft>
      <ConnectorLogoContainer
        size={32}
        borderRadius={8}
        hoverBackground={false}
      >
        <Icon name={icon} size={16} />
      </ConnectorLogoContainer>
      <Text variant="text-regular-md" ellipsis>
        {title}
      </Text>
      {space ? <SpaceBadge space={space} /> : null}
    </ModalHeaderLeft>
  );
}

export const ModalBody = styled("div")`
  flex: 1;
  min-height: 0;
  padding: 16px;
  border-top: 0.5px solid var(--border-alpha);
  border-bottom: 0.5px solid var(--border-alpha);
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ModalFooter = styled("div")`
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
